/**
 * UltimatumScreen1.tsx – First screen of Ultimatum Game (Unified Design)
 * Last updated: 2025-08-14
 * Unified translation, design & BlueprintJS compatibility
 */

import React, { useEffect, useState } from "react";
import { Slider, FormGroup, Icon } from "@blueprintjs/core";
import kinogutscheinImg from "./assets/Ultimatum_Kinogutschein.png";
import { ScreenProps } from "../../SequenceController";
import { useLanguage } from "../../../../context/LanguageContext";
import { Ultimatum } from "../../../../translations/experiments/Ultimatum";
import { colors, commonStyles } from "../../../../components/colors";

type ScreenClick = {
    amountForSelf: number;
    millisecondsSinceFirstRender: number;
};

type ScreenResult = {
    selections: Array<ScreenClick>;
    usedHelp: boolean;
};

export const Screen7InitialResult: ScreenResult = {
    selections: [],
    usedHelp: false
};

export const UltimatumScreen1 = (props: ScreenProps) => {
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [slider, setSlider] = useState(10); // Start in the middle (0-20)
    const { setCanContinue, setResult } = props;
    const { language } = useLanguage();
    const t = Ultimatum[language].screens.screen1;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    useEffect(() => {
        setCanContinue(true);
        setResult((result: ScreenResult) => {
            result.selections = [];
            result.selections.push({
                amountForSelf: slider,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp
            });
            return result;
        });
    }, [slider, setCanContinue, setResult, firstRenderTimestamp]);

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <div style={commonStyles.card}>
                {/* Headline */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t.title}
                </h2>
                {/* Paragraphs and story */}
                <div style={{
                    ...commonStyles.paragraph,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    {t.paragraphs.map((p: string, i: number) => (
                        <p key={i} style={{ margin: i === 0 ? "0 0 14px 0" : "18px 0 0 0" }}>
                            {p}
                        </p>
                    ))}
                </div>
                {/* Orange box with Icon and rules */}
                <div style={{
                    backgroundColor: colors.ocker,
                    border: `1px solid ${colors.ockerLight}`,
                    padding: "14px 20px",
                    borderRadius: 8,
                    margin: "32px 0 24px 0",
                    fontSize: 16,
                    fontWeight: 500,
                    color: colors.white,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    boxShadow: "0 2px 8px 0 rgba(215,125,0,0.14)"
                }}>
                    <Icon icon="info-sign" style={{ marginRight: 9, fontSize: 18, marginTop: 2 }} />
                    <span>
                        <ul style={{
                            margin: 0,
                            paddingLeft: 20,
                            fontSize: 16,
                            color: colors.white,
                            fontWeight: 500,
                            listStyle: "disc"
                        }}>
                            {t.list.map((item: string, idx: number) => (
                                <li key={idx} dangerouslySetInnerHTML={{ __html: item }} style={{ marginBottom: 5 }} />
                            ))}
                        </ul>
                    </span>
                </div>
                {/* One more paragraph under orange box */}
                <div style={{
                    ...commonStyles.paragraph,
                    textAlign: "justify",
                    textJustify: "inter-word",
                    marginTop: 0,
                    marginBottom: 0
                }}>
                    <p style={{ marginTop: 0, marginBottom: 0 }}>{t.paragraph2}</p>
                </div>
                {/* Image */}
                <div style={{ textAlign: "center", margin: "40px 0 0 0" }}>
                    <img
                        src={kinogutscheinImg}
                        alt={t.imgAlt}
                        style={{
                            maxWidth: 340,
                            width: "100%",
                            height: "auto",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)"
                        }}
                    />
                </div>
                {/* Centered Question above slider */}
                <div style={{ textAlign: "center", margin: "40px 0 18px 0" }}>
                    <span style={{
                        color: colors.brillantBlue,
                        fontSize: 22,
                        fontWeight: 600,
                        textAlign: "center"
                    }}>{t.sliderLabel}</span>
                </div>
                {/* Slider */}
                <FormGroup
                    label={undefined}
                    labelFor="slider-input"
                    style={{
                        marginTop: 0,
                        marginBottom: 30
                    }}
                >
                    <div style={{ maxWidth: 540, margin: "0 auto" }}>
                        <Slider
                            min={0}
                            max={20}
                            stepSize={1}
                            labelStepSize={5}
                            value={slider}
                            onChange={setSlider}
                            labelRenderer={num => `${num} € / ${20 - num} €`}
                            intent={undefined}
                            className="no-track-slider"
                        />
                    </div>
                </FormGroup>
                {/* Slider Result */}
                <p style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: colors.textDark,
                    fontSize: 16
                }}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t.sliderResultText(slider, 20 - slider)
                        }}
                    />
                </p>
            </div>
        </div>
    );
};